import * as React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PhoneIcon from '@mui/icons-material/Phone';
import { withTranslation } from 'react-i18next';
import EmployeeFormDialog from './EmployeeFormDialog';
import EmployeeDetailsDialog from './EmployeeDetailsDialog';

// Employee Card Component
function EmployeeCard({ employee, t, onClick }) {
  const initials = employee.name
    ? employee.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : '?';

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 3,
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        {/* Initials Avatar */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            bgcolor: 'rgba(49, 130, 206, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 600,
            color: 'secondary.main',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {initials}
        </Box>
        {/* Status Badge */}
        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 500,
            bgcolor: employee.state === 'active' || !employee.state ? '#e6ffed' : '#fee2e2',
            color: employee.state === 'active' || !employee.state ? '#48bb78' : '#fc8181',
          }}
        >
          {employee.state === 'active' || !employee.state ? t('Active') : t('Inactive')}
        </Box>
      </Box>

      {/* Employee Name */}
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: 'text.primary',
          mb: 0.5,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {employee.name}
      </Typography>

      {/* Role */}
      <Typography
        sx={{
          fontSize: '14px',
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {employee.title || t(`role_${employee.role || 'employee'}`)}
      </Typography>

      {/* Phone */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '14px',
          color: 'secondary.main',
          mb: 2,
        }}
      >
        <PhoneIcon fontSize="small" />
        {employee.phone_number || t('No phone')}
      </Box>

      {/* Active Jobs Counter */}
      <Box
        sx={{
          p: 1.5,
          px: 2,
          bgcolor: 'background.default',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
          {t('Active jobs')}
        </Typography>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {employee.active_jobs_count || 0}
        </Typography>
      </Box>
    </Paper>
  );
}

function MainEmployeesView({ t }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/employees/');
      // Handle both paginated and non-paginated responses
      const data = response.data.results || response.data;
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleEmployeeCreated = (newEmployee) => {
    setEmployees((prev) => [newEmployee, ...prev]);
  };

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setDetailsDialogOpen(true);
  };

  const handleEmployeeUpdated = () => {
    fetchEmployees();
  };

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{t('Error')}: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="employees-page">
      {/* Header - Responsive */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          component="h4"
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Employees')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          data-testid="add-employee-button"
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
            '&:hover': {
              bgcolor: 'secondary.light',
              boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
            },
          }}
        >
          {t('Add employee')}
        </Button>
      </Box>

      {/* Employee Cards Grid */}
      {loading ? (
        <Grid container spacing={2.5}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : employees.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('No employees yet')}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {employees.map((employee) => (
            <Grid item xs={12} sm={6} md={4} key={employee.id}>
              <EmployeeCard
                employee={employee}
                t={t}
                onClick={() => handleEmployeeClick(employee)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <EmployeeFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onEmployeeCreated={handleEmployeeCreated}
      />

      <EmployeeDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        onEmployeeUpdated={handleEmployeeUpdated}
      />
    </Box>
  );
}

export default withTranslation()(MainEmployeesView);
